/**
 * PATH: packages/tenant-shared/src/preferences/useDbViewPreference.ts
 * FILE_ID: TENANTSHARED-DBVIEWPREF-HOOK-001
 * PURPOSE: Bind React consumers to the shared identity-safe preference store.
 * ROLE IN ARCHITECTURE: React adapter above quotechViewPreferenceAdapter.
 * MAIN EXPORTS: useDbViewPreference and its additive compatibility contract.
 * NON-RESPONSIBILITIES: Does not implement HTTP, storage, timers, or revisions.
 * NOTES FOR FUTURE AI: Pass authenticated identity whenever the host has it.
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'

import type {
  ViewPreferenceConflict,
  ViewPreferenceIdentity,
  ViewPreferenceSyncState,
} from '@quotech/api/view-preferences'

import type { CoerceFn } from './dbViewPreference'
import {
  getQuotechViewPreferenceStore,
  resolveViewPreferenceIdentity,
} from './quotechViewPreferenceAdapter'

export interface UseDbViewPreferenceOptions<T extends object> {
  /** Opaque, stable namespace for this slice, e.g. `'kanban:board:42'`.
   *  Changing it mid-mount swaps to a different stored slice. */
  scopeKey: string
  /** Fully-populated default state for operators who have never saved. */
  defaults: T
  /** Validate/normalise an untrusted blob (localStorage OR server).
   *  Omit for a shallow `{...defaults, ...raw}` merge. MUST be stable
   *  (define at module scope or memoise) — it's captured once on mount. */
  coerce?: CoerceFn<T>
  /** Debounce window for the server PATCH. Default 700ms. */
  debounceMs?: number
  /** Persisted codec version sent with every full-blob write. Default 1. */
  schemaVersion?: number
  /** Explicit tenant and authenticated user cache boundary. */
  identity?: ViewPreferenceIdentity
  /** When false, skips the one-shot server hydrate (localStorage-only).
   *  Useful before the tenant/session id is known. Default true. */
  hydrateFromServer?: boolean
}

export interface UseDbViewPreferenceReturn<T extends object> {
  /** Current state. Cached value on first paint, then server-hydrated. */
  value: T
  /** Replace the whole state (value or updater). Caches + saves. */
  setValue: (next: T | ((prev: T) => T)) => void
  /** Shallow-merge a partial into the current state. Caches + saves. */
  patch: (partial: Partial<T>) => void
  /** Reset to `defaults` everywhere (local cache + server row). */
  reset: () => void
  /** True once the one-shot server hydrate has settled. */
  hydrated: boolean
  /** Additive status for callers that can expose durable sync health. */
  syncState: ViewPreferenceSyncState
  /** Present only after a stale If-Match write; local value remains unchanged. */
  conflict: ViewPreferenceConflict<T> | null
  /** Human-readable boundary failure; local state remains available. */
  error: string | null
  /** Retry a non-conflict transport failure. */
  retry: () => void
  /** Explicitly retry the preserved draft against the conflict revision. */
  retryConflict: () => void
  /** Explicitly discard the draft and accept the conflict snapshot. */
  discardConflict: () => void
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function defaultCoerce<T extends object>(defaults: T, raw: unknown): T {
  if (!raw || typeof raw !== 'object') return clone(defaults)
  return { ...clone(defaults), ...(raw as Partial<T>) }
}

/**
 * Subscribe a React consumer to one shared tenant/user/scope preference node.
 *
 * Inputs: stable scope, defaults/codec, identity, debounce, and hydrate policy.
 * Output: useState-compatible mutations plus durable sync/conflict state.
 * Side effects: local cache writes and one shared remote hydrate/sync.
 * Failure modes: failures stay in return state and never discard local value.
 */
export function useDbViewPreference<T extends object>(
  options: UseDbViewPreferenceOptions<T>,
): UseDbViewPreferenceReturn<T> {
  const {
    scopeKey,
    defaults,
    coerce,
    debounceMs,
    schemaVersion = 1,
    identity: suppliedIdentity,
    hydrateFromServer = true,
  } = options
  const identity = useMemo(
    () => suppliedIdentity ?? resolveViewPreferenceIdentity(),
    [suppliedIdentity],
  )
  const store = useMemo(
    () =>
      getQuotechViewPreferenceStore<T>({
        identity,
        scopeKey,
        schemaVersion,
        defaults,
        coerce: (raw) => (coerce ? coerce(raw, defaults) : defaultCoerce(defaults, raw)),
        debounceMs,
      }),
    [coerce, debounceMs, defaults, identity, schemaVersion, scopeKey],
  )
  const snapshot = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  )

  useEffect(() => {
    if (hydrateFromServer) void store.hydrate()
  }, [hydrateFromServer, store])

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const current = store.getSnapshot().value
      const resolved =
        typeof next === 'function' ? (next as (previous: T) => T)(current) : next
      store.setValue(resolved)
    },
    [store],
  )

  const patch = useCallback(
    (partial: Partial<T>) => {
      setValue((prev) => ({ ...prev, ...partial }))
    },
    [setValue],
  )

  const reset = useCallback(() => store.reset(), [store])
  const retry = useCallback(() => store.retry(), [store])
  const retryConflict = useCallback(() => store.retryConflict(), [store])
  const discardConflict = useCallback(() => store.discardConflict(), [store])

  return {
    value: snapshot.value,
    setValue,
    patch,
    reset,
    hydrated: !hydrateFromServer || snapshot.hydrated,
    syncState: snapshot.syncState,
    conflict: snapshot.conflict,
    error: snapshot.error,
    retry,
    retryConflict,
    discardConflict,
  }
}
